/**
 * Sprint 207Q — "Peer Helps — Last 24h" Command Centre tile.
 *
 * Reads enriched `peer_trophy_minted` audit rows from
 * GET /api/peer-invites/admin/last-24h and renders:
 *
 *   • inviter handle    — who earned the Support Trophy
 *   • invitee handle    — the friend whose completion triggered the mint
 *   • challenge title   — which 20-challenge milestone fired the hook
 *   • Polygonscan link  — direct tx_hash deep-link (only when minted on-chain)
 *   • mint_status badge — pending / minted / failed
 */
import React, { useEffect, useState } from 'react';
import { Trophy, ExternalLink, Loader2, Users, AlertCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

const STATUS_COLOR = {
  pending: { bg: '#FEF3C7', fg: '#92400E', label: 'pending' },
  minted:  { bg: '#D1FAE5', fg: '#065F46', label: 'minted'  },
  failed:  { bg: '#FEE2E2', fg: '#991B1B', label: 'failed'  },
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

const PeerHelpsCard = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/peer-invites/admin/last-24h');
        if (cancelled) return;
        setRows(data.rows || []);
        setCount(data.count || 0);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load Peer Helps');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm"
      data-testid="peer-helps-card"
    >
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: '#FEF3C715' }}>
            <Trophy size={18} className="text-amber-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Peer Helps — Last 24h</h3>
            <p className="text-xs text-gray-500">Support Trophy NFTs minted from peer-invite completions</p>
          </div>
        </div>
        <span
          className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-50 text-amber-700"
          data-testid="peer-helps-count"
        >
          {count} {count === 1 ? 'mint' : 'mints'}
        </span>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-sm text-red-600 py-3" data-testid="peer-helps-error">
            <AlertCircle size={16} /> {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm" data-testid="peer-helps-empty">
            <Users size={28} className="mx-auto mb-2 opacity-50" />
            No peer trophies minted in the last 24 hours. Once a teen invites a
            friend and that friend completes a challenge, the mint will surface here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4 font-semibold">When</th>
                  <th className="py-2 pr-4 font-semibold">Inviter</th>
                  <th className="py-2 pr-4 font-semibold">Invitee</th>
                  <th className="py-2 pr-4 font-semibold">Challenge</th>
                  <th className="py-2 pr-4 font-semibold">Status</th>
                  <th className="py-2 font-semibold">On-chain</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const status = STATUS_COLOR[r.mint_status] || STATUS_COLOR.pending;
                  return (
                    <tr
                      key={`${r.inviter_user_id}-${r.invitee_user_id}-${r.run_id}-${idx}`}
                      className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50"
                      data-testid={`peer-helps-row-${idx}`}
                    >
                      <td className="py-2.5 pr-4 text-xs text-gray-500">{fmtTime(r.timestamp)}</td>
                      <td className="py-2.5 pr-4 font-semibold text-gray-900" data-testid={`peer-helps-inviter-${idx}`}>{r.inviter_handle}</td>
                      <td className="py-2.5 pr-4 text-gray-700" data-testid={`peer-helps-invitee-${idx}`}>{r.invitee_handle}</td>
                      <td className="py-2.5 pr-4 text-gray-700 truncate max-w-xs" title={r.challenge_title} data-testid={`peer-helps-challenge-${idx}`}>{r.challenge_title}</td>
                      <td className="py-2.5 pr-4">
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
                          style={{ backgroundColor: status.bg, color: status.fg }}
                          data-testid={`peer-helps-status-${idx}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="py-2.5">
                        {r.polygonscan_url ? (
                          <a
                            href={r.polygonscan_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline font-medium"
                            data-testid={`peer-helps-polygonscan-${idx}`}
                          >
                            View tx <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400" data-testid={`peer-helps-no-tx-${idx}`}>
                            awaiting mint
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeerHelpsCard;
